import React, { useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../../store/languageSlice';
import { cvData } from '../../data/cvData';
import { translations } from '../../data/translations';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Experience.css';

gsap.registerPlugin(ScrollTrigger);

const MOBILE_MQ = '(max-width: 768px)';
const pad = (n, total) => `${String(n).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;

const ExperienceStepContent = ({ step }) => {
  if (step.type === 'role') {
    return (
      <>
        <span className="exp-bg-num" aria-hidden>01</span>
        <p className="exp-period">{step.period}</p>
        <h2 className="exp-role">{step.role}</h2>
        <p className="exp-company">/ {step.company}</p>
        <div className="exp-divider" />
        <p className="exp-desc">{step.description}</p>
      </>
    );
  }

  return (
    <>
      <span className="exp-bg-num" aria-hidden>
        {step.type === 'capabilities' ? '02' : '03'}
      </span>
      <p className="exp-period">{step.eyebrow}</p>
      <h2 className="exp-role exp-role--compact">{step.title}</h2>
      <div className="exp-divider" />
      <p className="exp-desc exp-desc--intro">{step.intro}</p>

      <div className="exp-groups">
        {step.groups.map((group) => (
          <div key={group.label} className="exp-group">
            <h3 className="exp-group-label">{group.label}</h3>
            <ul className="exp-tag-list">
              {group.items.map((item) => (
                <li key={item} className="exp-tag">{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
};

const Experience = () => {
  const language = useSelector(selectLanguage);
  const data = cvData[language];
  const t = translations[language];
  const { experienceSteps } = data;

  const sectionRef = useRef(null);
  const lineFillRef = useRef(null);
  const counterRef = useRef(null);
  const stepsRef = useRef([]);
  const dotsRef = useRef([]);
  const currentIdx = useRef(0);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const total = experienceSteps.length;
    const ACCENT = '#FE5B00';
    const mq = window.matchMedia(MOBILE_MQ);
    let ctx;

    const setupDesktop = () => {
      stepsRef.current.forEach((step, i) => {
        if (!step) return;
        gsap.set(step, { opacity: i === 0 ? 1 : 0, y: i === 0 ? 0 : 60 });
      });

      dotsRef.current.forEach((dot, i) => {
        if (!dot) return;
        gsap.set(dot, {
          xPercent: -50,
          scale: i === 0 ? 1.4 : 1,
          transformOrigin: 'center center',
          backgroundColor: i === 0 ? ACCENT : 'var(--border-color)',
        });
      });

      gsap.set(lineFillRef.current, {
        xPercent: -50,
        scaleY: 0,
        transformOrigin: 'top center',
      });

      if (counterRef.current) counterRef.current.textContent = pad(1, total);
      currentIdx.current = 0;

      const gotoStep = (newIdx) => {
        if (newIdx === currentIdx.current) return;

        const prevIdx = currentIdx.current;
        currentIdx.current = newIdx;
        const dir = newIdx > prevIdx ? 1 : -1;

        if (counterRef.current) {
          counterRef.current.textContent = pad(newIdx + 1, total);
        }

        const prev = stepsRef.current[prevIdx];
        if (prev) {
          gsap.to(prev, {
            opacity: 0,
            y: -55 * dir,
            duration: 0.38,
            ease: 'power2.in',
            overwrite: true,
          });
        }

        const next = stepsRef.current[newIdx];
        if (next) {
          gsap.fromTo(
            next,
            { opacity: 0, y: 55 * dir },
            { opacity: 1, y: 0, duration: 0.55, ease: 'power3.out', delay: 0.12, overwrite: true },
          );
        }

        dotsRef.current.forEach((dot, i) => {
          if (!dot) return;
          gsap.to(dot, {
            xPercent: -50,
            scale: i === newIdx ? 1.4 : 1,
            transformOrigin: 'center center',
            backgroundColor: i === newIdx ? ACCENT : 'var(--border-color)',
            duration: 0.3,
            overwrite: true,
          });
        });
      };

      ScrollTrigger.create({
        trigger: section,
        pin: true,
        pinSpacing: true,
        pinType: 'fixed',
        start: 'top top',
        end: `+=${Math.max(total - 1, 1) * window.innerHeight * 0.85}`,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate(self) {
          if (lineFillRef.current) {
            gsap.set(lineFillRef.current, {
              xPercent: -50,
              scaleY: self.progress,
              transformOrigin: 'top center',
            });
          }

          const raw = self.progress * total;
          const idx = Math.min(Math.floor(raw), total - 1);
          gotoStep(idx);
        },
        onLeave: () => {
          if (lineFillRef.current) {
            gsap.set(lineFillRef.current, {
              xPercent: -50,
              scaleY: 1,
              transformOrigin: 'top center',
            });
          }
        },
      });
    };

    const setupMobile = () => {
      stepsRef.current.forEach((step) => {
        if (!step) return;
        gsap.set(step, { clearProps: 'all' });
      });

      dotsRef.current.forEach((dot) => {
        if (!dot) return;
        gsap.set(dot, { clearProps: 'all' });
      });

      if (lineFillRef.current) {
        gsap.set(lineFillRef.current, { clearProps: 'all' });
      }

      stepsRef.current.forEach((step, i) => {
        if (!step) return;

        // Elementi interni animabili
        const marker    = step.querySelector('.exp-step-marker');
        const period    = step.querySelector('.exp-period');
        const role      = step.querySelector('.exp-role');
        const company   = step.querySelector('.exp-company');
        const divider   = step.querySelector('.exp-divider');
        const desc      = step.querySelector('.exp-desc');
        const groups    = step.querySelectorAll('.exp-group');
        const inner     = [marker, period, role, company, divider, desc].filter(Boolean);

        // Stato iniziale per l'intero blocco
        gsap.set(step, { opacity: 0, x: i % 2 === 0 ? -40 : 40 });
        gsap.set(inner, { opacity: 0, y: 20 });
        groups.forEach((g) => gsap.set(g, { opacity: 0, y: 24, scale: 0.97 }));

        // Linea decorativa: parte da 0 e si allunga
        const borderLine = step.querySelector('.exp-divider');
        if (borderLine) gsap.set(borderLine, { scaleX: 0, transformOrigin: 'left center' });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: step,
            start: 'top 82%',
            toggleActions: 'play none none reverse',
          },
        });

        // 1. Slide-in del blocco intero
        tl.to(step, { opacity: 1, x: 0, duration: 0.5, ease: 'power3.out' });

        // 2. Stagger degli elementi interni
        tl.to(inner, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.07,
          ease: 'power2.out',
        }, '-=0.25');

        // 3. Linea che si allunga
        if (borderLine) {
          tl.to(borderLine, {
            scaleX: 1,
            duration: 0.45,
            ease: 'power2.inOut',
          }, '-=0.35');
        }

        // 4. Gruppi competenze con leggero scale
        if (groups.length > 0) {
          tl.to(groups, {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.45,
            stagger: 0.1,
            ease: 'back.out(1.3)',
          }, '-=0.2');
        }
      });
    };

    const setup = () => {
      if (ctx) ctx.revert();

      section.classList.toggle('exp-root--stack', mq.matches);

      ctx = gsap.context(() => {
        if (mq.matches) {
          setupMobile();
        } else {
          setupDesktop();
        }
      }, section);

      ScrollTrigger.refresh();
    };

    setup();
    mq.addEventListener('change', setup);

    return () => {
      mq.removeEventListener('change', setup);
      section.classList.remove('exp-root--stack');
      if (ctx) ctx.revert();
    };
  }, [language, experienceSteps.length]); // eslint-disable-line

  const total = experienceSteps.length;
  const dotPercents = experienceSteps.map((_, i) => ((i + 0.5) / total) * 100);
  const sectionTitle = t.experience?.title || (language === 'it' ? 'Esperienza' : 'Experience');

  return (
    <section
      id="experience"
      ref={sectionRef}
      className="exp-root"
    >
      <header className="exp-topbar">
        <span className="exp-label">{sectionTitle}</span>
        <span ref={counterRef} className="exp-counter">
          {pad(1, total)}
        </span>
      </header>

      <div className="exp-body">
        <div className="exp-timeline" aria-hidden="true">
          <div className="exp-line-track" />
          <div ref={lineFillRef} className="exp-line-fill" />

          {experienceSteps.map((step, i) => (
            <div
              key={step.timelineLabel}
              className="exp-dot-row"
              style={{ top: `${dotPercents[i]}%` }}
            >
              <div
                ref={(el) => { dotsRef.current[i] = el; }}
                className="exp-dot"
              />
              <span className="exp-dot-period">{step.timelineLabel}</span>
            </div>
          ))}
        </div>

        <div className="exp-content">
          {experienceSteps.map((step, i) => (
            <article
              key={step.timelineLabel}
              ref={(el) => { stepsRef.current[i] = el; }}
              className={`exp-step ${step.type !== 'role' ? 'exp-step--groups' : ''}`}
            >
              <p className="exp-step-marker">{step.timelineLabel}</p>
              <ExperienceStepContent step={step} />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
